import React, { useEffect, useState } from 'react';
import { adminService, AvailableItem } from '../services/adminService';
import './AdminPanel.css';

export default function AvailableItemsAdmin() {
  const [items, setItems] = useState<AvailableItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [similar, setSimilar] = useState<AvailableItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await adminService.getAvailableItems();
        setItems(data);
      } catch (err) {
        setError('Fehler beim Laden der Items');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const q = newName.trim().toLowerCase();
    if (!q) {
      setSimilar([]);
      return;
    }
    const matches = items.filter((it) =>
      it.name.toLowerCase().includes(q) || q.includes(it.name.toLowerCase())
    ).slice(0, 5);
    setSimilar(matches);
  }, [newName, items]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    setError('');
    try {
      const created = await adminService.createAvailableItem(newName.trim());
      setItems((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name, 'de')));
      setNewName('');
      setSimilar([]);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Fehler beim Anlegen';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Wirklich löschen?')) return;
    try {
      await adminService.deleteAvailableItem(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (err) {
      setError('Fehler beim Löschen');
    }
  };

  const handleStartEdit = (item: AvailableItem) => {
    setError('');
    setEditing({ id: item.id, name: item.name });
  };

  const handleCancelEdit = () => {
    setEditing(null);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing || !editing.name.trim()) return;
    setSavingEdit(true);
    setError('');
    try {
      const updated = await adminService.updateAvailableItem(editing.id, editing.name.trim());
      setItems((prev) =>
        prev
          .map((it) => (it.id === editing.id ? updated : it))
          .sort((a, b) => a.name.localeCompare(b.name, 'de'))
      );
      setEditing(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Fehler beim Umbenennen';
      setError(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) return <div className="loading">Lädt...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="admin-panel">
      <h1>Verfügbare Artikel (Katalog)</h1>
      <p className="helper-text">Nur Admin: Übersicht aller kanonischen Items aus available_items.json</p>

      <form className="admin-form" onSubmit={handleAdd}>
        <label>Neuer Name</label>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="z. B. Kompresse 10x10"
          required
        />
        {similar.length > 0 && (
          <div className="helper-text">
            Ähnliche vorhanden: {similar.map((s) => s.name).join(', ')}
          </div>
        )}
        <button type="submit" className="btn-primary" disabled={saving}>Hinzufügen</button>
      </form>
      <table className="admin-table">
        <thead>
          <tr>
            <th style={{ width: '30%' }}>Name</th>
            <th style={{ width: '30%' }}>ID (fix)</th>
            <th style={{ width: '200px' }}>Aktion</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                {editing?.id === item.id ? (
                  <form onSubmit={handleSaveEdit} className="inline-edit-form">
                    <input
                      className="inline-input"
                      type="text"
                      value={editing.name}
                      onChange={(e) => setEditing((prev) => prev ? { ...prev, name: e.target.value } : prev)}
                      autoFocus
                    />
                    <div className="inline-actions">
                      <button className="btn-primary" type="submit" disabled={savingEdit}>
                        {savingEdit ? 'Speichert…' : 'Speichern'}
                      </button>
                      <button type="button" className="btn-secondary" onClick={handleCancelEdit} disabled={savingEdit}>
                        Abbrechen
                      </button>
                    </div>
                    <div className="helper-text">ID bleibt unverändert</div>
                  </form>
                ) : (
                  <>
                    <div>{item.name}</div>
                    <div className="helper-text">Anzeigename</div>
                  </>
                )}
              </td>
              <td>
                <div>{item.id}</div>
                <div className="helper-text">konstante ID</div>
              </td>
              <td>
                {editing?.id === item.id ? (
                  <span className="helper-text">Bearbeitung läuft…</span>
                ) : (
                  <div className="actions-cell" style={{ gap: '0.35rem' }}>
                    <button className="btn-secondary" onClick={() => handleStartEdit(item)}>Umbenennen</button>
                    <button className="btn-delete" onClick={() => handleDelete(item.id)}>Entfernen</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
